import nearestPointOnLine from '@turf/nearest-point-on-line'
import { pointToPolygonDistance } from "@turf/point-to-polygon-distance"
import { point, lineString } from '@turf/helpers'
import bearing from '@turf/bearing'
import distance from '@turf/distance'

// stolen from George
export function getClosestPointPolyline({lat,lng}, polylines) {
    if (!lat || !lng || !polylines || !polylines.length) {
        return null
    }
    const pt = point([lng, lat])

    let closest = null
    let minDist = Infinity

    polylines.forEach(polyline => {
        const coords = Array.from({ length: polyline.getPath().getLength() }, (_, i) => {
            const p = polyline.getPath().getAt(i)
            return [p.lng(), p.lat()]
        })

        const line = lineString(coords)

        const snapped = nearestPointOnLine(line, pt)
        const dist = snapped.properties.dist || 0

        if (dist < minDist) {
            minDist = dist
            closest = snapped
        }

    })

    if (closest) {
        const [lng, lat] = closest.geometry.coordinates

        return {
            lat: lat,
            lng: lng
        }
    }
    return null
}

export function getDistancePointToPolygon({ lat, lng }, polygon) {
    if (!lat || !lng || !polygon) {
        return null
    }

    if (polygon.type !== 'Polygon' && polygon.type !== 'MultiPolygon') {
        console.warn('Provided geometry is not a polygon')
        return null
    }
    const pt = point([lng, lat])
    const dist = pointToPolygonDistance(pt, polygon, { units: 'meters' })
    return dist
}

// Normalize angle to [0, 360)
function normalizeAngle(angle) {
    return ((angle % 360) + 360) % 360
}

// Smallest angle between two bearings (0-180)
function angleDiff(a, b) {
    const diff = Math.abs(normalizeAngle(a) - normalizeAngle(b))
    return Math.min(diff, 360 - diff)
}

/**
 * Check if polygon is rectangle (4 corners, 90deg angles, opposite sides equal).
 * @param {Feature<Polygon>} feature - GeoJSON polygon feature
 * @param {number} toleranceDeg - angle tolerance in degrees (default 3)
 * @returns {boolean}
 */
export function isRectangle(feature, toleranceDeg = 3) {
    if (!feature || feature.geometry?.type !== 'Polygon') {
        return false
    }

    const coordinates = feature.geometry.coordinates?.[0]
    if (!coordinates || coordinates.length !== 5) {
        return false
    }

    const [p1, p2, p3, p4, p5] = coordinates

    // First and last point must match (closed ring)
    if (p1[0] !== p5[0] || p1[1] !== p5[1]) {
        return false
    }

    const b1 = bearing(point(p1), point(p2))
    const b2 = bearing(point(p2), point(p3))
    const b3 = bearing(point(p3), point(p4))
    const b4 = bearing(point(p4), point(p1))

    // Adjacent sides must be perpendicular
    const angle12 = angleDiff(b1, b2)
    const angle23 = angleDiff(b2, b3)
    const angle34 = angleDiff(b3, b4)
    const angle41 = angleDiff(b4, b1)

    const isRightAngles =
        Math.abs(angle12 - 90) <= toleranceDeg &&
        Math.abs(angle23 - 90) <= toleranceDeg &&
        Math.abs(angle34 - 90) <= toleranceDeg &&
        Math.abs(angle41 - 90) <= toleranceDeg

    if (!isRightAngles) {
        return false
    }

    // Opposite sides must have similar length (5% tolerance)
    const d1 = distance(point(p1), point(p2))
    const d2 = distance(point(p2), point(p3))
    const d3 = distance(point(p3), point(p4))
    const d4 = distance(point(p4), point(p1))

    const lengthTolerance = 0.05

    return (
        Math.abs(d1 - d3) / Math.max(d1, d3) <= lengthTolerance &&
        Math.abs(d2 - d4) / Math.max(d2, d4) <= lengthTolerance
    )
}

/**
 * Normalize a point to `{ lat, lng }`.
 * Accepts Google Maps LatLng (`lat()`/`lng()`), GeoJSON `[lng, lat]`, or `{ lat, lng }`.
 */
function toLatLngLiteral(point) {
    if (!point) return null
    if (typeof point.lat === 'function' && typeof point.lng === 'function') {
        return { lat: point.lat(), lng: point.lng() }
    }
    if (Array.isArray(point)) {
        return { lat: point[1], lng: point[0] }
    }
    if (point.lat != null && point.lng != null) {
        return { lat: point.lat, lng: point.lng }
    }
    return null
}

export function stripClosingDuplicatePoint(paths) {
    // Strip closing duplicate point — GeoJSON always closes rings
    // (first === last), but Google Maps Polygon adds its own closing
    // edge and would show a redundant vertex if we keep the duplicate.
    if (!paths || paths.length < 2) {
        return paths
    }

    const first = toLatLngLiteral(paths[0])
    const last = toLatLngLiteral(paths[paths.length - 1])
    if (first && last && first.lat === last.lat && first.lng === last.lng) {
        return paths.slice(0, -1)
    }

    return paths
}

export function geoJsonRingToOpenPath(ring) {
    if (!ring) return []
    return stripClosingDuplicatePoint(
        ring.map(coord => ({
            lat: coord[1],
            lng: coord[0],
        }))
    )
}
